import { createServerFn } from "@tanstack/react-start";

type GuardInput = { email: string; action: "signup" | "reset" | "beta" };

function friendly(retryAfter: number): string {
  const mins = Math.ceil(Math.max(retryAfter, 1) / 60);
  return `Too many attempts. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`;
}

/**
 * Rate gate for the public auth actions (sign-up, password reset).
 * Limits per IP and per email address; the client must call this first.
 */
export const guardAuthAction = createServerFn({ method: "POST" })
  .inputValidator((data: GuardInput) => ({
    email: String(data?.email ?? "").trim().slice(0, 200).toLowerCase(),
    action:
      data?.action === "signup"
        ? ("signup" as const)
        : data?.action === "beta"
          ? ("beta" as const)
          : ("reset" as const),
  }))
  .handler(async ({ data }) => {
    const { rateLimit, callerKey, hashedKey } = await import("./rate-limit.server");

    if (data.action === "beta") {
      // Open beta: anyone may take a free seat in exchange for weekly feedback.
      const betaIp = await rateLimit(`beta:ip`, callerKey(), 5, 3600);
      if (!betaIp.allowed) {
        return {
          ok: false as const,
          retryAfter: betaIp.retryAfter,
          message: friendly(betaIp.retryAfter),
        };
      }
      const byEmail = await rateLimit(`beta:email`, hashedKey(data.email), 3, 86400);
      if (!byEmail.allowed) {
        return {
          ok: false as const,
          retryAfter: byEmail.retryAfter,
          message: friendly(byEmail.retryAfter),
        };
      }

      // No approval is written here: the seat is granted by syncEarlyAccess
      // once the person has confirmed they own the email address.
      return { ok: true as const, retryAfter: 0, message: "" };
    }

    if (data.action === "signup") {
      // Early access: the email must be approved by an admin, or the visitor
      // must have unlocked the private-beta access password.
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: request } = await supabaseAdmin
        .from("early_access_requests")
        .select("status")
        .ilike("email", data.email)
        .maybeSingle();
      const approved = request?.status === "approved";

      if (!approved) {
        const { isUnlocked } = await import("./site-gate.server");
        if (!(await isUnlocked())) {
          return {
            ok: false as const,
            retryAfter: 0,
            message:
              "Accounts are invite-only right now. Request early access and we'll email you when it's ready.",
          };
        }
      }
      // signup: 5 per hour per IP
      const signupIp = await rateLimit(`signup:ip`, callerKey(), 5, 3600);
      if (!signupIp.allowed) {
        return {
          ok: false as const,
          retryAfter: signupIp.retryAfter,
          message: friendly(signupIp.retryAfter),
        };
      }
      return { ok: true as const, retryAfter: 0, message: "" };
    }

    // reset: 3 per 10 min per IP, 3 per hour per email
    const byIp = await rateLimit(`reset:ip`, callerKey(), 3, 600);
    if (!byIp.allowed) {
      return { ok: false as const, retryAfter: byIp.retryAfter, message: friendly(byIp.retryAfter) };
    }
    if (data.email) {
      const byEmail = await rateLimit(`reset:email`, hashedKey(data.email), 3, 3600);
      if (!byEmail.allowed) {
        return {
          ok: false as const,
          retryAfter: byEmail.retryAfter,
          message: friendly(byEmail.retryAfter),
        };
      }
    }
    return { ok: true as const, retryAfter: 0, message: "" };
  });
